import { intersection, isEmpty, isEqual, isMatch, pick } from 'lodash-es';
import { useObservableState } from 'observable-hooks';
import PropTypes from 'prop-types';
import {
    asyncScheduler,
    combineLatest,
    debounce,
    distinctUntilChanged,
    map,
    scheduled,
} from 'rxjs';
import { SwarmType } from '../../enums';
import { MultiSensorDataSet } from '../../graph/dataset';
import { shareReplayRefCount } from '../../utils/rxjs';
import { ConfigHelper } from '../pages/measuringPoint/configHelper';
import Form from '../reactForm/Form';
import { Layout, PlainGroupContainer } from '../reactForm/Layout';
import { fromReactHookFormWatch } from '../reactForm/utils';
import { SwarmFormController } from '../pages/measuringPoint/form-controller';
import { LayoutTree } from '../reactForm/layout-helper';

const OVERRIDE_OPTION = Object.freeze({
    AUTO: 'auto',
    MANUAL: 'manual',
});

const OVERRIDE_OPTION_TRANSLATIONS = Object.freeze({
    [OVERRIDE_OPTION.AUTO]: gettext('AUTO'),
    [OVERRIDE_OPTION.MANUAL]: gettext('CUSTOM'),
});

const GUIDE_LINE_FIELD_NAME = 'guideLine';
const OVERRIDE_FIELD_NAME = 'override';

class OverridesConfigHelper extends ConfigHelper {
    transformConfigFields(configFields) {
        // Hack the `override` field into the choice table.
        return new Map([
            [
                OVERRIDE_FIELD_NAME,
                new Map([
                    [OVERRIDE_OPTION.AUTO, new Map()],
                    [OVERRIDE_OPTION.MANUAL, super.transformConfigFields(configFields)],
                ]),
            ],
        ]);
    }
}

// This overrides form is hardcoded to only be compatible with SWARM Vibration.
const overridesFormSwarmType = SwarmType.Vibration;

class OverriddenFormSwarmController extends SwarmFormController {
    constructor({ dataSet, setOverrides, ...extraConfig }) {
        super(extraConfig);

        const guideLineFields = Object.keys(
            this.layoutHelper.getFieldsStartingAt(GUIDE_LINE_FIELD_NAME)
        );

        const config$ = dataSet.config$.pipe(
            // As this config is being used in the form we need
            // to distance convert it.
            map((config) => this.convertConfigValues(config)),
            shareReplayRefCount()
        );
        const values$ = fromReactHookFormWatch(this.rhfProps.formControl);

        this.showForm$ = config$.pipe(map((config) => Boolean(config)));
        this.dataSet = dataSet;

        // Update the form values when the sensor config changes.
        config$.subscribe((config) => {
            this.rhfProps.formControl.reset(config, { keepDefaultValues: false });
        });

        combineLatest([values$, config$, this.configHelper.availableFields$])
            .pipe(
                map(([values, config, availableFields]) => {
                    // There is no override when `auto` is selected.
                    if (values[OVERRIDE_FIELD_NAME] !== OVERRIDE_OPTION.MANUAL) {
                        return null;
                    }

                    const currentlyAvailableGuidelineFields = intersection(
                        guideLineFields,
                        Object.keys(availableFields)
                    );

                    // Grab the form values that are displayed by the config helper.
                    const overriddenConfig = pick(values, currentlyAvailableGuidelineFields);

                    // There is no override when the form values match the sensor config.
                    if (isEmpty(overriddenConfig) || isMatch(config, overriddenConfig)) {
                        return null;
                    }

                    return overriddenConfig;
                }),
                debounce(() => scheduled([undefined], asyncScheduler)),
                distinctUntilChanged(isEqual),
                // Use the revert distance converter to get everything back to millimeters.
                map((config) => this.convertConfigValues(config, true))
            )
            .subscribe((overrides) => {
                setOverrides(overrides);
            });
    }

    createConfigHelper() {
        return new OverridesConfigHelper(this);
    }

    createLayoutTree() {
        // Search for the layout group that contains the guideline fields.
        const guideLineGroup = super
            .createLayoutTree()
            .groups.getByName('swarmSettings')
            .groups.getByName('swarmSettingsMeasurement');

        // Replace the guideline field (since it is not possible and does not make sense to
        // switch guideline on the overrides form anyway) with the 'Overrides enable' toggle.
        guideLineGroup.fields.replaceByName(GUIDE_LINE_FIELD_NAME, {
            name: OVERRIDE_FIELD_NAME,
            label: gettext('SETTINGS'),
            type: 'choice',
            options: Object.entries(OVERRIDE_OPTION_TRANSLATIONS),
            default: OVERRIDE_OPTION.AUTO,
        });

        return new LayoutTree({
            name: 'root',
            groups: [guideLineGroup],
        });
    }

    onSubmitFunction() {
        // No-op: overrides are emitted on change; submitting should not persist anything.
        return undefined;
    }
}

function OverridesForm({ dataSet, setOverrides }) {
    const formController = OverriddenFormSwarmController.use(overridesFormSwarmType, false, {
        dataSet,
        setOverrides,
    });

    const showForm = useObservableState(formController.showForm$);

    if (!showForm) {
        return null;
    }

    return (
        <Form formController={formController} showSaveButton={false}>
            <Layout formController={formController} GroupContainer={PlainGroupContainer} />
        </Form>
    );
}

OverridesForm.propTypes = {
    dataSet: PropTypes.instanceOf(MultiSensorDataSet).isRequired,
    setOverrides: PropTypes.func,
};

export default OverridesForm;
